import type {
  DSDCenterData,
  DSDCurrencyData,
  IncludedClient,
  SplitBlock,
  VolumeRow,
} from '../types'

// =============================================================================
// Consolidated (€) view for DSD Center — combines EUR + USD. Money series are
// converted at each month's average rate and summed; unit counts sum directly
// (units don't convert); percentages are recomputed. Period-total figures with
// no monthly breakdown use the window-average rate.
// =============================================================================

const toEur = (usd: number, rate: number) => (rate ? usd / rate : 0)
const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 1)

export function consolidate(d: DSDCenterData): DSDCurrencyData {
  const E = d.data.EUR
  const U = d.data.USD
  const months = d.months
  const rate = (m: string) => d.fxRates[m] ?? 1
  const winAvg = mean(months.map(rate))

  // Combine two per-month money series (aligned to `months`).
  const sumMoney = (e: number[], u: number[]) => e.map((v, i) => v + toEur(u[i] ?? 0, rate(months[i])))
  const sumUnits = (e: number[], u: number[]) => e.map((v, i) => v + (u[i] ?? 0))

  const zeros = months.map(() => 0)
  // Merge two keyed money series (category / stage / lab ids) — union of keys.
  const mergeKeyed = (e?: Record<string, number[]>, u?: Record<string, number[]>) => {
    if (!e && !u) return undefined
    const out: Record<string, number[]> = {}
    for (const k of new Set([...Object.keys(e ?? {}), ...Object.keys(u ?? {})])) out[k] = sumMoney(e?.[k] ?? zeros, u?.[k] ?? zeros)
    return out
  }
  // The v2 split only (byCategory / byStage / byLab / unclassified); the legacy
  // byClass is no longer read, so it is not carried into the consolidated view.
  const block = (be: SplitBlock, bu: SplitBlock): SplitBlock => ({
    total: sumMoney(be.total, bu.total),
    byCategory: mergeKeyed(be.byCategory, bu.byCategory),
    byStage: mergeKeyed(be.byStage, bu.byStage),
    byLab: mergeKeyed(be.byLab, bu.byLab),
    unclassified: be.unclassified || bu.unclassified ? sumMoney(be.unclassified ?? zeros, bu.unclassified ?? zeros) : undefined,
  })
  const split = {
    gross: block(E.split.gross, U.split.gross),
    pcCredit: block(E.split.pcCredit, U.split.pcCredit),
    trueSale: block(E.split.trueSale, U.split.trueSale),
  }

  const madeAvailable = sumMoney(E.utilisation.madeAvailable, U.utilisation.madeAvailable)
  const used = sumMoney(E.utilisation.used, U.utilisation.used)
  const utilisation = {
    madeAvailable,
    used,
    manuallyRemoved: sumMoney(E.utilisation.manuallyRemoved, U.utilisation.manuallyRemoved),
    usedPct: madeAvailable.map((a, i) => (a ? Math.round((used[i] / a) * 100) : 0)),
  }

  const Em = E.volume.monthly
  const Um = U.volume.monthly
  const totalUnits = sumUnits(Em.totalUnits, Um.totalUnits)
  const includedUnits = sumUnits(Em.includedUnits, Um.includedUnits)
  const monthly = {
    paidUnits: sumUnits(Em.paidUnits, Um.paidUnits),
    includedUnits,
    totalUnits,
    cashCollected: sumMoney(Em.cashCollected, Um.cashCollected),
    pcCredit: sumMoney(Em.pcCredit, Um.pcCredit),
    totalSettled: sumMoney(Em.totalSettled, Um.totalSettled),
    includedShare: totalUnits.map((t, i) => (t ? Math.round((includedUnits[i] / t) * 100) : 0)),
  }
  // Merge two lists of volume rows by id (EUR order first, then any USD-only id):
  // units sum directly, money converts at each month's rate.
  const mergeRows = (e?: VolumeRow[], u?: VolumeRow[]): VolumeRow[] | undefined => {
    if (!e && !u) return undefined
    const uById = new Map((u ?? []).map((r) => [r.id, r]))
    const eIds = new Set((e ?? []).map((r) => r.id))
    const pair = (er: VolumeRow | undefined, ur: VolumeRow | undefined): VolumeRow => {
      const base = (er ?? ur) as VolumeRow
      return {
        id: base.id,
        name: base.name,
        department: base.department,
        paidUnits: sumUnits(er?.paidUnits ?? zeros, ur?.paidUnits ?? zeros),
        includedUnits: sumUnits(er?.includedUnits ?? zeros, ur?.includedUnits ?? zeros),
        paidCardInv: sumMoney(er?.paidCardInv ?? zeros, ur?.paidCardInv ?? zeros),
        paidPcCredit: sumMoney(er?.paidPcCredit ?? zeros, ur?.paidPcCredit ?? zeros),
        paidValue: sumMoney(er?.paidValue ?? zeros, ur?.paidValue ?? zeros),
      }
    }
    return [...(e ?? []).map((er) => pair(er, uById.get(er.id))), ...(u ?? []).filter((ur) => !eIds.has(ur.id)).map((ur) => pair(undefined, ur))]
  }
  const volume = {
    monthly,
    byCategory: mergeRows(E.volume.byCategory, U.volume.byCategory),
    byStage: mergeRows(E.volume.byStage, U.volume.byStage),
    byDepartment: mergeRows(E.volume.byDepartment, U.volume.byDepartment),
    includedValue: sumMoney(E.volume.includedValue, U.volume.includedValue),
  }

  const invoicing = {
    invoiced: sumMoney(E.invoicing.invoiced, U.invoicing.invoiced),
    afterWindow: E.invoicing.afterWindow + toEur(U.invoicing.afterWindow, winAvg),
    pipeline: sumMoney(E.invoicing.pipeline, U.invoicing.pipeline),
    priorYearDelivered:
      (E.invoicing.priorYearDelivered ?? 0) + toEur(U.invoicing.priorYearDelivered ?? 0, winAvg),
  }

  // Credit by client — convert USD clinics to €, then merge the two lists.
  const cmonths = E.creditByClient.months
  const crate = (m: string) => d.fxRates[m] ?? 1
  const usdClients = U.creditByClient.clinics.map((c) => ({
    clinic: c.clinic,
    fee: toEur(c.fee, winAvg),
    monthly: c.monthly.map((v, i) => toEur(v, crate(cmonths[i]))),
    total: c.monthly.reduce((a, v, i) => a + toEur(v, crate(cmonths[i])), 0),
  }))
  const creditByClient = {
    months: cmonths,
    clinics: [...E.creditByClient.clinics, ...usdClients].sort((a, b) => b.total - a.total),
  }

  return { split, utilisation, creditByClient, volume, invoicing }
}

/** Scale every aggregate series by a per-month fraction — estimates a selected
 *  client's (or clients') share of the board for the cards that have no real
 *  per-client breakdown. Ratios (usedPct, includedShare) are preserved because
 *  numerator and denominator scale together. */
export function scaleCurrency(c: DSDCurrencyData, f: number[]): DSDCurrencyData {
  const sa = (a: number[]) => a.map((v, i) => v * (f[i] ?? 1))
  const sk = (r?: Record<string, number[]>) => (r ? Object.fromEntries(Object.entries(r).map(([k, a]) => [k, sa(a)])) : undefined)
  const sb = (b: SplitBlock): SplitBlock => ({
    total: sa(b.total),
    byCategory: sk(b.byCategory),
    byStage: sk(b.byStage),
    byLab: sk(b.byLab),
    unclassified: b.unclassified ? sa(b.unclassified) : undefined,
  })
  const sr = (rows?: VolumeRow[]) =>
    rows?.map((r) => ({
      id: r.id,
      name: r.name,
      department: r.department,
      paidUnits: sa(r.paidUnits),
      includedUnits: sa(r.includedUnits),
      paidCardInv: sa(r.paidCardInv),
      paidPcCredit: sa(r.paidPcCredit),
      paidValue: sa(r.paidValue),
    }))
  return {
    split: { gross: sb(c.split.gross), pcCredit: sb(c.split.pcCredit), trueSale: sb(c.split.trueSale) },
    utilisation: {
      madeAvailable: sa(c.utilisation.madeAvailable),
      used: sa(c.utilisation.used),
      manuallyRemoved: sa(c.utilisation.manuallyRemoved),
      usedPct: c.utilisation.usedPct,
    },
    creditByClient: c.creditByClient,
    volume: {
      monthly: {
        paidUnits: sa(c.volume.monthly.paidUnits),
        includedUnits: sa(c.volume.monthly.includedUnits),
        totalUnits: sa(c.volume.monthly.totalUnits),
        cashCollected: sa(c.volume.monthly.cashCollected),
        pcCredit: sa(c.volume.monthly.pcCredit),
        totalSettled: sa(c.volume.monthly.totalSettled),
        includedShare: c.volume.monthly.includedShare,
      },
      byCategory: sr(c.volume.byCategory),
      byStage: sr(c.volume.byStage),
      byDepartment: sr(c.volume.byDepartment),
      includedValue: sa(c.volume.includedValue),
    },
    invoicing: { invoiced: sa(c.invoicing.invoiced), afterWindow: c.invoicing.afterWindow, pipeline: sa(c.invoicing.pipeline), priorYearDelivered: c.invoicing.priorYearDelivered },
  }
}

/** Inclusive-contract clients in €, converting the USD rows month-by-month. */
export function consolidateIncluded(d: DSDCenterData): IncludedClient[] {
  const rate = (m: string) => d.fxRates[m] ?? 1
  const conv = (arr: number[]) => arr.map((v, i) => Math.round(v / rate(d.months[i])))
  return d.includedClients.map((c) =>
    c.cur === 'USD'
      ? { ...c, cur: 'EUR', paidCardInv: conv(c.paidCardInv), paidPcCredit: conv(c.paidPcCredit), paidValue: conv(c.paidValue) }
      : c,
  )
}
